import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Job,
  MasterPlan,
} from 'master-locations-library/master-locations-library/src/lib/core/domain/models/master-plan';
import { BindLocationWithJobRequest } from 'master-locations-library/master-locations-library/src/lib/core/domain/requests';
import { GetStopsForPlanUsecase } from 'master-locations-library/master-locations-library/src/lib/core/usecases/location/get-stop-for-plan.usecase';
import { GetWayPointsForJobUsecase } from 'master-locations-library/master-locations-library/src/lib/core/usecases/location/get-way-points-for-job.usecase';
import { catchError, tap } from 'rxjs';
import { nameof } from 'ts-simple-nameof';
import {
  AdditionalDataKey,
  LoaderHandler,
  PrivateLocationsForPP,
} from '../../../../core/domain/models';
import {
  AddLocationWithJobUsecase,
  GetAdditionalDataKeysUseCase,
  GetAdditionalDataKeyUseCase,
  GetMasterPlanForMLSUsecase,
  GetMasterPlansByLocationIdAndProductIdUsecase,
  GetPlansByMasterPlanAndLocationIdUsecase,
  GetPlansByMasterPlanAndSegmentUsecase,
  GetPrivateLocationByMlsIdUsecase,
} from '../../../../core/usecases';
import {
  DisableLoading,
  EnableLoading,
} from '../../../base/state/base.actions';
import {
  BindLocationWithJobs,
  GetAdditionalDataKey,
  GetAdditionalDataKeys,
  GetJobByMasterPlanIdAndLocationId,
  GetJobByMasterPlanIdAndSegment,
  GetMasterPlanByLocationAndProduct,
  GetMasterPlanForMLS,
  GetPrivateLocationByMlsId,
  GetSelectedJobsForInjection,
  GetStopsForPlans,
  GetWayPointsForJob,
  RemoveJob,
} from './add-edit-private-location.actions';
import { LocationInfoForm } from './location-info-form.model';

export class AddEditPrivateLocationStateModel {
  public locationInfo!: {
    model: LocationInfoForm | undefined;
    dirty: boolean;
    status: string;
    errors: unknown;
  };
  public products?: {
    model: unknown | undefined;
    dirty: boolean;
    status: string;
    errors: unknown;
  };
  public masterPlans: MasterPlan[] | undefined;
  public additionalDataKeys: AdditionalDataKey[] | undefined;
  public additionalDataKey: AdditionalDataKey | undefined;
  public privateLocation: PrivateLocationsForPP | undefined;
  public plansStops: PrivateLocationsForPP[] | undefined;
  public jobs: Job[] | undefined;
  public getSelectedJobsForInjection: BindLocationWithJobRequest[] | undefined;
  public removeJob: Job[] | undefined;
  public IsLocationsAddedToJobs: boolean | undefined;
  public waypoints: number[][] | undefined;
}

const defaults = {
  locationInfo: {
    model: undefined,
    dirty: false,
    status: '',
    errors: {},
  },
  products: {
    model: undefined,
    dirty: false,
    status: '',
    errors: {},
  },
  masterPlans: undefined,
  additionalDataKeys: undefined,
  additionalDataKey: undefined,
  privateLocation: undefined,
  getSelectedJobsForInjection: undefined,
  removeJob: undefined,
  IsLocationsAddedToJobs: undefined,
  plansStops: undefined,
  jobs: undefined,
  waypoints: undefined,
};

@State<AddEditPrivateLocationStateModel>({
  name: 'mls_addEditPrivateLocation',
  defaults,
})
@Injectable()
export class AddEditPrivateLocationState {
  constructor(
    private getAdditionalDataKeysUseCase: GetAdditionalDataKeysUseCase,
    private getAdditionalDataKeyUseCase: GetAdditionalDataKeyUseCase,
    private getMasterPlansBySegmentUsecase: GetMasterPlanForMLSUsecase,
    private getMasterPlansByLocationIdAndProductIdUsecase: GetMasterPlansByLocationIdAndProductIdUsecase,
    private getPrivateLocationByMlsIdUsecase: GetPrivateLocationByMlsIdUsecase,
    private addLocationWithJobUsecase: AddLocationWithJobUsecase,
    private getStopsForPlanUsecase: GetStopsForPlanUsecase,
    private getWayPointsForJobUsecase: GetWayPointsForJobUsecase,
    private getPlansByMasterPlanAndSegmentUsecase: GetPlansByMasterPlanAndSegmentUsecase,
    private getPlansByMasterPlanAndLocationIdUsecase: GetPlansByMasterPlanAndLocationIdUsecase
  ) {}

  @Action(GetAdditionalDataKeys)
  getAdditionalDataKeys(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetAdditionalDataKeys
  ) {
    if (payload?.searchText !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.getAdditionalDataKeys
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getAdditionalDataKeysUseCase.execute(payload.searchText).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), additionalDataKeys: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), additionalDataKeys: undefined });
    }
  }

  @Action(GetAdditionalDataKey)
  getAdditionalDataKey(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetAdditionalDataKey
  ) {
    if (payload?.keyName) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.getAdditionalDataKey
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getAdditionalDataKeyUseCase.execute(payload.keyName).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), additionalDataKey: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), additionalDataKey: undefined });
    }
  }

  @Action(GetMasterPlanForMLS)
  GetMasterPlanForMLS(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetMasterPlanForMLS
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetMasterPlanForMLS
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getMasterPlansBySegmentUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), masterPlans: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), masterPlans: undefined });
    }
  }

  @Action(GetSelectedJobsForInjection)
  GetSelectedJobsForInjection(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetSelectedJobsForInjection
  ) {
    const state = ctx.getState();
    if (payload != undefined) {
      const currentJobs = state.getSelectedJobsForInjection || [];
      const idExists = currentJobs.some((job) => job.jobId === payload.jobId);
      if (!idExists) {
        let updatedJobs: BindLocationWithJobRequest[] = [];
        if (Array.isArray(state.getSelectedJobsForInjection)) {
          updatedJobs = [...state.getSelectedJobsForInjection, payload];
        } else {
          updatedJobs = [payload];
        }
        ctx.setState({
          ...state,
          getSelectedJobsForInjection: updatedJobs,
        });
      }
    } else {
      ctx.setState({
        ...state,
        getSelectedJobsForInjection: undefined,
      });
    }
  }

  @Action(RemoveJob)
  removeJob(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: RemoveJob
  ) {
    const state = ctx.getState();
    const currentJobs = state.getSelectedJobsForInjection || [];
    const filteredJobs = currentJobs.filter(
      (job) => job.jobId != payload.jobId
    );
    ctx.setState({
      ...state,
      getSelectedJobsForInjection: filteredJobs,
    });
  }

  @Action(GetMasterPlanByLocationAndProduct)
  GetMasterPlanByLocationAndProduct(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetMasterPlanByLocationAndProduct
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetMasterPlanByLocationAndProduct
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getMasterPlansByLocationIdAndProductIdUsecase
        .execute(payload)
        .pipe(
          tap((response) => {
            ctx.dispatch(new DisableLoading(loaderHandler));
            ctx.setState({ ...ctx.getState(), masterPlans: response });
          }),
          catchError((err: HttpErrorResponse) => {
            console.error(err);
            ctx.dispatch(new DisableLoading(loaderHandler));
            throw err;
          })
        );
    } else {
      return ctx.setState({ ...ctx.getState(), masterPlans: undefined });
    }
  }

  @Action(BindLocationWithJobs)
  BindLocationWithJobs(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: BindLocationWithJobs
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.BindLocationWithJobs
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.addLocationWithJobUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), IsLocationsAddedToJobs: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({
        ...ctx.getState(),
        IsLocationsAddedToJobs: undefined,
      });
    }
  }

  @Selector()
  static IsLocationsAddedToJobs(state: AddEditPrivateLocationStateModel) {
    return state.IsLocationsAddedToJobs;
  }
  @Selector()
  static GetMasterPlanList(state: AddEditPrivateLocationStateModel) {
    return state.masterPlans;
  }

  @Selector()
  static GetLocationInfo(state: AddEditPrivateLocationStateModel) {
    return state.locationInfo;
  }

  @Selector()
  static GetSelectedJobsForInjection(state: AddEditPrivateLocationStateModel) {
    return state.getSelectedJobsForInjection;
  }

  @Action(GetPrivateLocationByMlsId)
  GetPrivateLocationByMlsId(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetPrivateLocationByMlsId
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetPrivateLocationByMlsId
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getPrivateLocationByMlsIdUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), privateLocation: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), privateLocation: undefined });
    }
  }

  @Action(GetStopsForPlans)
  GetStopsForPlans(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetStopsForPlans
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetStopsForPlans
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getStopsForPlanUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), plansStops: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), plansStops: undefined });
    }
  }

  @Action(GetWayPointsForJob)
  GetWayPointsForJob(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetWayPointsForJob
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetWayPointsForJob
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getWayPointsForJobUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), waypoints: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), plansStops: undefined });
    }
  }

  @Action(GetJobByMasterPlanIdAndSegment)
  GetJobByMasterPlanIdAndSegment(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetJobByMasterPlanIdAndSegment
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetJobByMasterPlanIdAndSegment
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getPlansByMasterPlanAndSegmentUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.setState({ ...ctx.getState(), jobs: response });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), jobs: undefined });
    }
  }

  @Action(GetJobByMasterPlanIdAndLocationId)
  GetJobByMasterPlanIdAndLocationId(
    ctx: StateContext<AddEditPrivateLocationStateModel>,
    { payload }: GetJobByMasterPlanIdAndLocationId
  ) {
    if (payload !== undefined) {
      const loaderHandler: LoaderHandler = {
        componentName: AddEditPrivateLocationState.name,
        endPointName: nameof<AddEditPrivateLocationState>(
          (state) => state.GetJobByMasterPlanIdAndLocationId
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getPlansByMasterPlanAndLocationIdUsecase
        .execute(payload)
        .pipe(
          tap((response) => {
            ctx.dispatch(new DisableLoading(loaderHandler));
            ctx.setState({ ...ctx.getState(), jobs: response });
          }),
          catchError((err: HttpErrorResponse) => {
            console.error(err);
            ctx.dispatch(new DisableLoading(loaderHandler));
            throw err;
          })
        );
    } else {
      return ctx.setState({ ...ctx.getState(), jobs: undefined });
    }
  }

  @Selector()
  static GetStopsForPlans(state: AddEditPrivateLocationStateModel) {
    return state.plansStops;
  }

  @Selector()
  static GetJobsByMasterPlan(state: AddEditPrivateLocationStateModel) {
    return state.jobs;
  }

  @Selector()
  static GetJobWayPoints(state: AddEditPrivateLocationStateModel) {
    return state.waypoints;
  }
}
